import { Platform } from 'react-native';

const DB_NAME = 'workorder-offline-uploads';
const STORE_NAME = 'blobs';
const DB_VERSION = 1;

const isWeb = Platform.OS === 'web';

const hasIndexedDB = (): boolean => {
	return isWeb && typeof indexedDB !== 'undefined';
};

let _dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
	if (_dbPromise) return _dbPromise;
	_dbPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME);
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => {
			_dbPromise = null; // allow retry on open failure
			reject(request.error);
		};
	});
	return _dbPromise;
};

export const offlineBlobStore = {
	async put(id: string, blob: Blob): Promise<void> {
		if (!hasIndexedDB()) throw new Error('IndexedDB not available');
		const db = await openDb();
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction(STORE_NAME, 'readwrite');
			tx.objectStore(STORE_NAME).put(blob, id);
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(tx.error);
		});
	},

	async get(id: string): Promise<Blob | null> {
		if (!hasIndexedDB()) return null;
		const db = await openDb();
		return new Promise<Blob | null>((resolve, reject) => {
			const tx = db.transaction(STORE_NAME, 'readonly');
			const req = tx.objectStore(STORE_NAME).get(id);
			req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
			req.onerror = () => reject(req.error);
		});
	},

	async delete(id: string): Promise<void> {
		if (!hasIndexedDB()) return;
		const db = await openDb();
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction(STORE_NAME, 'readwrite');
			tx.objectStore(STORE_NAME).delete(id);
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
			tx.onabort = () => reject(tx.error);
		});
	},
};

export const isBlobStoreAvailable = hasIndexedDB;
